import type {
  Breakline,
  Workspace,
} from '@data-access/geospatial/fx-workspace-service';
import { returnOrThrow } from '@trimble/http-utils';

import type { FxBreakline } from './fx-workspace-breakline.types';
import type { FxWorkspace } from './fx-workspace.types';

export const DEFAULT_PAGE_SIZE = 100;
export const MAX_PAGES = 100;
export const HTTP_NOT_FOUND = 404;

export const CLIENT_NAME_HEADER = 'Trimble-FieldSystems-ClientName';
export const CLIENT_VERSION_HEADER = 'Trimble-FieldSystems-ClientVersion';

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value);
}

function hasNextPage(
  rawPageLength: number,
  fetchedCount: number,
  totalItems: number
): boolean {
  return rawPageLength === DEFAULT_PAGE_SIZE && fetchedCount < totalItems;
}

export async function fetchAllPages<T>(
  fetchPage: (pageIndex: number) => Promise<{
    items: readonly T[];
    totalItems: number | string;
  }>
): Promise<T[]> {
  const items: T[] = [];
  let pageIndex = 0;
  let fetchedCount = 0;
  let hasMore = true;

  while (hasMore) {
    if (pageIndex >= MAX_PAGES) {
      throw new Error(
        `Exceeded maximum of ${MAX_PAGES} pages while listing FX workspace data`
      );
    }

    const page = await fetchPage(pageIndex);
    fetchedCount += page.items.length;
    items.push(...page.items);

    hasMore = hasNextPage(
      page.items.length,
      fetchedCount,
      toNumber(page.totalItems)
    );
    pageIndex += 1;
  }

  return items;
}

export function getClientHeaders(
  clientName: string,
  clientVersion: string
): {
  [CLIENT_NAME_HEADER]: string;
  [CLIENT_VERSION_HEADER]: string;
} {
  return {
    [CLIENT_NAME_HEADER]: clientName,
    [CLIENT_VERSION_HEADER]: clientVersion,
  };
}

export function unwrapOrThrow<Data, Err>(
  response: { data: Data; error: undefined } | { data: undefined; error: Err },
  errorMessage: string
): NonNullable<Data> {
  // returnOrThrow throws when data is absent, but infers Data from both
  // union branches, so its return type widens to include undefined.
  return returnOrThrow(response, errorMessage) as NonNullable<Data>;
}

export function mapWorkspaceResponse(response: Workspace): FxWorkspace {
  return {
    id: response.workspaceTrn,
    name: response.name,
    jobs: [],
  };
}

export function mapBreaklineResponse(response: Breakline): FxBreakline {
  return {
    type: response.type,
    name: response.name,
    layerId: response.layerId,
  };
}
